import { LOCALES, STRINGS, type Locale } from "./strings.js";

export { LOCALES, LOCALE_LABEL, type Locale } from "./strings.js";

const STORAGE_KEY = "zg.locale";

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Pick a starting language: a shared link's ?lang= first, then a previously
 * saved choice, then the device. Assamese speakers should not have to find a
 * toggle, and everyone else should not be handed a script they cannot read.
 */
function detect(): Locale {
  // ?lang= wins: it only appears when somebody deliberately shared the link
  // in one language, and it is the most recent explicit signal we have.
  const param = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(param)) return param;

  const stored = safeRead(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const base = candidate.toLowerCase().split("-")[0];
    if (base === "as") return "as";
  }
  return "en";
}

function isLocale(value: string | null | undefined): value is Locale {
  return value === "en" || value === "as";
}

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode and locked-down browsers throw rather than return null.
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* a lost preference is not worth breaking the page over */
  }
}

let locale: Locale = detect();
document.documentElement.lang = locale;

export function getLocale(): Locale {
  return locale;
}

export function setLocale(next: Locale): void {
  if (!LOCALES.includes(next) || next === locale) return;
  locale = next;
  document.documentElement.lang = next;
  safeWrite(STORAGE_KEY, next);
  for (const listener of listeners) listener();
}

/** Re-run whenever the language changes. Returns an unsubscribe function. */
export function onLocaleChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Look up a string. Falls back to English, then to the key itself — a missing
 * translation should show readable English, never a blank button.
 */
export function t(key: string): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key;
}
